'use client';

import { MsalProvider } from "@azure/msal-react";
import { msalInstance } from "@/lib/auth-config";
import { usePathname, useRouter } from 'next/navigation';
import { MenuProvider } from "@/contexts/menu-context";
import { useEffect, useState } from "react";
import { LoadingScreen } from "@/components/loading-screen";
import { useEvent } from "@/hooks/use-event";
import { AppSpinner } from "@/components/layout/app-spinner";
import { AuthError } from "@azure/msal-browser";

export default function LayoutAuthWrapper({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const isLoginPage = pathname === '/login' || pathname === '/login/';
  const isNoAccessPage = pathname === '/no-access';
  const isReportSharePage = pathname === '/share/report';
  const [isMsalInitialized, setIsMsalInitialized] = useState(false);
  const [isMenuLoaded, setIsMenuLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [shouldRedirectToLogin, setShouldRedirectToLogin] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Subscribe to menu loaded event
  useEvent('menu:loaded', () => {
    setIsMenuLoaded(true);
    setIsLoading(false);
  });

  // Subscribe to menu no access event
  useEvent('menu:noAccess', () => {
    if (!isRedirecting) {
      setShouldRedirectToLogin(true);
    }
  });

  // Handle redirects in an effect
  useEffect(() => {
    if (shouldRedirectToLogin && !isLoginPage && !isRedirecting && !isReportSharePage && pathname !== '/redirect') {
      setIsRedirecting(true);
      localStorage.setItem('intendedUrl', pathname);
      router.push('/login');
    }
  }, [shouldRedirectToLogin, router, isLoginPage, isRedirecting, isReportSharePage, pathname]);

  // Reset states when on login or no-access page
  useEffect(() => {
    if (isLoginPage || isNoAccessPage) {
      setIsLoading(false);
      setShouldRedirectToLogin(false);
      setIsRedirecting(false);
    }
  }, [isLoginPage, isNoAccessPage]);

  // Initialize MSAL
  useEffect(() => {
    let mounted = true;

    const initializeMsal = async () => {
      try {
        // Check if MSAL is already initialized
        try {
          // This will throw if not initialized
          msalInstance.getAllAccounts();
          // If we get here, MSAL is already initialized
          if (mounted) {
            setIsMsalInitialized(true);
          }
        } catch (error) {
          // Only initialize if not already initialized
          if (error instanceof AuthError &&
            error.errorCode === 'uninitialized_public_client_application') {
            await msalInstance.initialize();

            if (!mounted) return;

            setIsMsalInitialized(true);
          } else {
            // If it's some other error, we still need to handle it
            if (mounted) {
              setIsMsalInitialized(true); // Set to true to avoid blocking UI
            }
            throw error;
          }
        }

        // Check if we have any accounts after initialization
        try {
          const accounts = msalInstance.getAllAccounts();

          if (accounts.length === 0 && !isLoginPage && !isRedirecting && !isReportSharePage) {
            setIsLoading(false);
            setShouldRedirectToLogin(true);
          } else if (accounts.length > 0 && !isLoginPage && !isNoAccessPage && !isReportSharePage) {
            // We have accounts and we're not on login/no-access page
            setIsLoading(true);
          } else {
            // We're either on login page or no-access page
            setIsLoading(false);
          }
        } catch (accountError) {
          if (!isLoginPage && !isRedirecting && !isReportSharePage) {
            setIsLoading(false);
            setShouldRedirectToLogin(true);
          }
        }
      } catch (error) {
        if (!mounted) return;

        setIsMsalInitialized(true);
        setIsLoading(false);
        if (!isLoginPage && !isRedirecting && !isReportSharePage) {
          setShouldRedirectToLogin(true);
        }
      }
    };

    initializeMsal();

    return () => {
      mounted = false;
    };
  }, [isLoginPage, isNoAccessPage, isRedirecting, isReportSharePage]);

  // Show initialization loading screen
  if (!isMsalInitialized) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <AppSpinner
            size="lg"
            variant="default"
            message="Initializing application..."
          />
        </div>
      </div>
    );
  }

  // After initialization, check if we need to show login page
  let accounts = [];
  try {
    accounts = msalInstance.getAllAccounts();
  } catch (error) {
    // Continue with empty accounts array
  }

  // Wrap everything in MsalProvider
  return (
    isReportSharePage ? (
      children
    ) : (
      <MsalProvider instance={msalInstance}>
        {isLoginPage || isNoAccessPage ? (
          // Don't use MenuProvider on login or no-access page
          children
        ) : accounts.length > 0 ? (
          // Wrap authenticated content in MenuProvider
          <MenuProvider>
            {isLoading ? (
              <LoadingScreen
                message="Loading your application..."
                fadeOut={isMenuLoaded}
              />
            ) : (
              children
            )}
          </MenuProvider>
        ) : (
          // Show loading screen with fade-out animation while redirecting to login
          <LoadingScreen message="Redirecting to login..." fadeOut={isRedirecting} />
        )}
      </MsalProvider>
    )
  );
}
